'use client';

import React, { PropsWithChildren, useEffect } from 'react';
import { Provider, ErrorBoundary, useRollbar } from '@rollbar/react';
import type Rollbar from 'rollbar';
import { getRollbarConfig } from '@/utils/rollbar';
import ErrorView from '@/views/Error/Error';
import { User } from '@/typings/user';

type RollbarProviderProps = PropsWithChildren<{
  instance?: Rollbar;
  user?: User | null;
}>;

const RollbarPersonTracking: React.FC<{ user?: User | null }> = ({ user }) => {
  const rollbar = useRollbar();

  useEffect(() => {
    if (user && rollbar) {
      rollbar.configure({
        payload: {
          person: {
            id: user.id,
            email: user.email,
            username: user.name,
          },
        },
      });
    } else if (!user && rollbar) {
      // Clear person data when user logs out
      rollbar.configure({
        payload: {
          person: undefined,
        },
      });
    }
  }, [user, rollbar]);

  return null;
};

const RollbarProvider: React.FC<RollbarProviderProps> = ({ children, instance, user }) => {
  return (
    <Provider
      {...(instance ? { instance } : { config: getRollbarConfig(true) })}
      instance={instance}
    >
      <RollbarPersonTracking user={user} />
      <ErrorBoundary
        errorMessage="Error in React render"
        fallbackUI={ErrorView} // Rollbar is wrongly typed (https://github.com/rollbar/rollbar-react/issues/87)
        extra={(_error, _info) => ({
          // Extra information that need to be sent to rollbar
        })}
      >
        {children}
      </ErrorBoundary>
    </Provider>
  );
};

export default RollbarProvider;
